/**
 * AgentActionsWidget — actions de réunion assignées à l'agent (Lot 5).
 * Affichées dans le cockpit agent avec case à cocher pour marquer fait.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

function fdate(iso) {
    return iso ? new Date(iso).toLocaleDateString('fr-FR') : '';
}

export default function AgentActionsWidget() {
    const [actions, setActions] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/pilotage/actions?status=open');
            setActions(r.data.actions || []);
        } catch (e) { setActions([]); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const markDone = async (a) => {
        setActions((list) => list.filter((x) => x.id !== a.id));
        try {
            await api.patch(`/pilotage/actions/${a.id}`, { status: 'done' });
            toast.success('Action terminée — bien joué !');
        } catch (e) {
            toast.error('Mise à jour impossible');
            load();
        }
    };

    if (!actions || actions.length === 0) return null;

    const isOverdue = (a) => a.due_date && new Date(a.due_date) < new Date();

    return (
        <div data-testid="agent-actions-widget"
             className="rounded-xl p-4"
             style={{ background: 'var(--homly-bg-elevated)',
                      border: '1px solid var(--homly-border)',
                      borderRadius: 'var(--homly-radius-md)' }}>
            <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-4 h-4" style={{ color: '#A855F7' }} />
                <span className="text-sm font-semibold">Mes actions de réunion</span>
                <span data-testid="agent-actions-count"
                      className="px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white"
                      style={{ background: '#681793' }}>
                    {actions.length}
                </span>
            </div>
            <ul className="space-y-2">
                {actions.map((a) => (
                    <li key={a.id} className="flex items-start gap-2.5"
                        data-testid={`agent-action-row-${a.id}`}>
                        <button type="button" onClick={() => markDone(a)}
                                data-testid={`agent-action-done-${a.id}`}
                                aria-label="Marquer comme fait"
                                className="mt-0.5 group">
                            <Circle className="w-4.5 h-4.5 w-5 h-5 group-hover:hidden"
                                    style={{ color: 'var(--homly-text-muted, #888)' }} />
                            <CheckCircle2 className="w-5 h-5 hidden group-hover:block text-emerald-500" />
                        </button>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm leading-snug">{a.title}</p>
                            {a.due_date && (
                                <p className="text-[11px]"
                                   style={isOverdue(a)
                                       ? { color: '#f87171', fontWeight: 600 }
                                       : { color: 'var(--homly-text-muted, #888)' }}>
                                    Échéance {fdate(a.due_date)}{isOverdue(a) && ' — en retard'}
                                </p>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
